import React, { useCallback, useMemo, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";

import RRDDirectMailAssetFilter from "./RRDDirectMailAssetFilter";
import { rrdDirectMailCampaignHelper } from "@/features/campaigns/direct-mail/rrd/utils/rrdDirectMailHelper";
import NoAssetFoundMsg from "./NoAssetFoundMsg";
import {
  getRRDAssetInstruction,
  getRRDAssetPanelSections,
} from "./rrdAssetPanelHelper";
import type { RRDAsset, RRDAssetGroups } from "./rrdTemplateTypes";

function RRdDirectMailMobileAssets({
  heroImages = [],
  onSelectImage,
  assets,
  clickType,
  printType,
  layoutKey,
  isBulk = false,
}: {
  heroImages?: RRDAsset[];
  onSelectImage: (_data: { type: string; asset: RRDAsset }) => void;
  assets?: RRDAssetGroups;
  clickType?: string;
  printType?: string;
  layoutKey?: string | null;
  isBulk?: boolean;
}) {
  const [selectedLabel, setSelectedLabel] = useState<unknown[]>([]);

  const filteredAssets = useMemo(() => {
    return rrdDirectMailCampaignHelper.getFilteredAssets(assets, selectedLabel);
  }, [assets, selectedLabel]);

  const handleOnSelectLabel = useCallback((v: unknown[]) => {
    setSelectedLabel(v);
  }, []);

  const isHeroDisabled = useCallback(
    (asset?: RRDAsset) => {
      return heroImages?.find((h) => h?.id === asset?.id) ? true : false;
    },
    [heroImages]
  );

  const sections = useMemo(
    () => getRRDAssetPanelSections({ printType, layoutKey }),
    [printType, layoutKey]
  );

  const instruction = useMemo(
    () => getRRDAssetInstruction({ printType, layoutKey, isBulk }),
    [printType, layoutKey, isBulk]
  );

  return (
    <Box sx={{ minHeight: "50vh" }}>
      <RRDDirectMailAssetFilter
        assets={assets}
        onSelectLabel={handleOnSelectLabel}
        disablePortal
      />
      <Typography variant="caption" sx={{ my: 1 }}>
        {instruction}
      </Typography>
      {sections.map((section) => (
        <Box
          key={section.assetType}
          sx={{ display: clickType !== section.assetType ? "none" : "block" }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: "bold", mt: 2 }}
            gutterBottom
          >
            {section.label}
          </Typography>
          <Grid container spacing={1}>
            {filteredAssets?.[section.key]?.length ? (
              filteredAssets[section.key].map((item) => {
                const disabled =
                  section.key !== "coupons" && isHeroDisabled(item);
                return (
                  <Grid
                    size={{
                      xs: "auto",
                    }}
                    key={item.id}
                  >
                    <Box
                      component="img"
                      src={item.compress_url}
                      alt={item.asset_name}
                      loading="lazy"
                      sx={{
                        filter: disabled ? "opacity(0.4)" : "none",
                        cursor: disabled
                          ? "not-allowed!important"
                          : "grab!important",
                        width: 100,
                        height: 100,
                        objectFit: "contain",
                        backgroundColor: "var(--neutral-white)",
                        borderRadius: "var(--radius-control)",
                        border: "1px solid var(--neutral-200)",
                        ":hover": {
                          boxShadow: "none",
                        },
                      }}
                      onClick={() =>
                        disabled
                          ? null
                          : onSelectImage({
                              type: section.assetType,
                              asset: {
                                ...item,
                                asset_type: section.assetType,
                                assetType: section.assetType,
                              },
                            })
                      }
                    />
                  </Grid>
                );
              })
            ) : (
              <NoAssetFoundMsg type={section.emptyType} />
            )}
          </Grid>
        </Box>
      ))}
    </Box>
  );
}

export default RRdDirectMailMobileAssets;
